import { DesiredRule, DesiredStateDiff } from '../lib/types';
import { Empty } from './ui';

const LABELS: Record<string, string> = {
  name: 'Name',
  description: 'Description',
  entraClientId: 'Entra client id',
  allowClientCredentials: 'Application permissions',
  allowDelegated: 'Delegated access',
  allowApiKey: 'API key access',
  allowSelfManagement: 'Self-management',
  redirectUris: 'Redirect addresses',
  ipAllowlist: 'Reachable from',
  ownerEmail: 'Owner',
};

/**
 * What would change. Fields first, then the permissions module by module —
 * an approval is a decision about the second half far more often than the
 * first, so the rules are spelled out rather than counted.
 */
export function ChangeRequestDiff({ diff }: { diff: DesiredStateDiff }) {
  const modules = diff.modules.filter((entry) => entry.change !== 'unchanged');
  if (diff.isEmpty) return <Empty>This request changes nothing.</Empty>;

  return (
    <>
      {diff.fields.length ? (
        <table className="diff">
          <thead>
            <tr>
              <th>Field</th>
              <th>Before</th>
              <th>After</th>
            </tr>
          </thead>
          <tbody>
            {diff.fields.map((change) => (
              <tr key={change.field}>
                <td>{LABELS[change.field] ?? change.field}</td>
                <td className="muted">{render(change.before)}</td>
                <td>
                  <strong>{render(change.after)}</strong>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {modules.map((entry) => (
        <div key={entry.moduleKey} className="diff-module">
          <h4>
            <span className="mono">{entry.moduleKey}</span>{' '}
            <span className="muted tiny">
              {entry.change === 'added'
                ? 'module added'
                : entry.change === 'removed'
                  ? 'module removed'
                  : 'permissions changed'}
            </span>
          </h4>
          <ul className="rules">
            {entry.addedRules.map((rule) => (
              <li key={`+${ruleKey(rule)}`} className="added">
                + {rule.resource} · {rule.action} · {rule.effect}
              </li>
            ))}
            {entry.removedRules.map((rule) => (
              <li key={`-${ruleKey(rule)}`} className="removed">
                − {rule.resource} · {rule.action} · {rule.effect}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </>
  );
}

function ruleKey(rule: DesiredRule): string {
  return `${rule.resource}:${rule.action}:${rule.effect}`;
}

function render(value: unknown): string {
  if (value === null || value === undefined || value === '') return '—';
  if (Array.isArray(value)) return value.length ? value.join(', ') : '—';
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}
